/**
 * /onboard/setup/import/:runId/preview
 *
 * Shows the upload's basic counts (from the freshly created
 * import_run) and a Continue button that:
 *   1. Calls promote-import-run for chunk 0..N-1 sequentially.
 *   2. Calls analyze-import-run.
 *   3. Navigates to /onboard/setup/import/:runId/inferences.
 *
 * Polls the import_run row throughout so the progress panel
 * stays in sync with the back-end state machine even if the
 * user opens the page mid-flight or refreshes.
 *
 * Gated by importEnabledForInternal. Off → redirect to
 * /onboard/setup (FirstLoginJunction).
 *
 * Core-100 R4 (CORE-V):
 *   - Rows the parser could not read are listed (row, field, reason — never a
 *     value) and the owner/manager must acknowledge them before anything is
 *     imported (acknowledge_import_unresolved_rows). The server refuses
 *     promotion until then; the button only mirrors that rule.
 *   - A failed run can be discarded through the governed, audited
 *     discard_failed_import_run (a reason is required). Until then none of
 *     its partial history is shown anywhere (RLS hides unfinished runs).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useImportRun } from '@/hooks/onboarding/useImportRun';
import { ImportProgressPanel } from '@/components/onboarding/import/ImportProgressPanel';

type Stage = 'idle' | 'promoting' | 'analyzing' | 'done' | 'error';

type RpcClient = {
  rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }>;
};

const OnboardSetupImportPreview = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const { activeShop, user } = useAuth();
  const importEnabled = useFeatureFlag('importEnabledForInternal');

  const [stage, setStage] = useState<Stage>('idle');
  const [actionError, setActionError] = useState<string | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [discardReason, setDiscardReason] = useState('');
  const [discardBusy, setDiscardBusy] = useState(false);

  const { run, isLoading, isError, refetch } = useImportRun({
    shopId: activeShop?.id ?? null,
    importRunId: runId ?? null,
    enabled: Boolean(importEnabled && activeShop?.id && runId),
  });

  // Flag gate.
  useEffect(() => {
    if (importEnabled === false) {
      navigate('/onboard/setup', { replace: true });
    }
  }, [importEnabled, navigate]);

  // Auto-navigate to inferences once analyze completes — covers
  // both the click-driven path and the "user refreshes after
  // backend already moved on" path.
  useEffect(() => {
    if (run?.status === 'analyzed' && stage !== 'error') {
      navigate(`/onboard/setup/import/${runId}/inferences`, { replace: true });
    }
  }, [run?.status, stage, navigate, runId]);

  if (!user || !activeShop) return null;
  if (!runId) {
    return (
      <div className="mx-auto max-w-2xl p-4" data-testid="import-preview-missing-run">
        <Card>
          <CardHeader>
            <CardTitle>Import not found</CardTitle>
            <CardDescription>Missing run id. Return to setup and try again.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleContinue = async () => {
    if (!run) return;
    setActionError(null);
    setStage('promoting');
    try {
      // Promote every chunk sequentially. The handler is
      // idempotent on retry — restarting a chunk after a network
      // blip just re-marks the staging rows promoted.
      const totalChunks = Math.max(run.chunk_total, 1);
      for (let chunk = run.chunk_promoted; chunk < totalChunks; chunk++) {
        const { data, error } = await supabase.functions.invoke('promote-import-run', {
          body: {
            shop_id: activeShop.id,
            import_run_id: runId,
            chunk_index: chunk,
          },
        });
        if (error) {
          setActionError(error.message ?? 'Promotion failed');
          setStage('error');
          return;
        }
        const body = (data ?? {}) as { ok?: boolean; error?: string };
        if (body.ok === false) {
          setActionError(body.error ?? `Chunk ${chunk} failed`);
          setStage('error');
          return;
        }
      }

      // Analyze.
      setStage('analyzing');
      const { data: analyzeData, error: analyzeError } = await supabase.functions.invoke(
        'analyze-import-run',
        {
          body: { shop_id: activeShop.id, import_run_id: runId },
        },
      );
      if (analyzeError) {
        setActionError(analyzeError.message ?? 'Analysis failed');
        setStage('error');
        return;
      }
      const analyzeBody = (analyzeData ?? {}) as { ok?: boolean; error?: string };
      if (analyzeBody.ok === false) {
        setActionError(analyzeBody.error ?? 'Analysis failed');
        setStage('error');
        return;
      }
      setStage('done');
      navigate(`/onboard/setup/import/${runId}/inferences`, { replace: true });
    } catch (e) {
      setActionError(`Unexpected error: ${(e as Error).message ?? String(e)}`);
      setStage('error');
    }
  };

  const handleAcknowledge = async () => {
    if (!run) return;
    setActionError(null);
    setReviewBusy(true);
    const { error } = await (supabase as unknown as RpcClient).rpc('acknowledge_import_unresolved_rows', {
      p_import_run_id: runId,
    });
    setReviewBusy(false);
    if (error) {
      setActionError(error.message ?? 'Could not record your review');
      return;
    }
    await refetch();
  };

  const handleDiscard = async () => {
    if (!run) return;
    setActionError(null);
    setDiscardBusy(true);
    const { error } = await (supabase as unknown as RpcClient).rpc('discard_failed_import_run', {
      p_import_run_id: runId,
      p_reason: discardReason.trim(),
    });
    setDiscardBusy(false);
    if (error) {
      setActionError(error.message ?? 'Could not discard this import');
      return;
    }
    navigate('/onboard/setup', { replace: true });
  };

  const unresolvedCount = run?.rows_unresolved ?? 0;
  const needsReview = unresolvedCount > 0 && !run?.unresolved_acknowledged_at;
  const isWorking = stage === 'promoting' || stage === 'analyzing';
  const canContinue =
    !!run &&
    !isWorking &&
    !needsReview &&
    (run.status === 'uploaded' || run.status === 'promoting' || run.status === 'promoted');

  return (
    <div
      className="mx-auto w-full max-w-3xl space-y-6 p-4"
      data-testid="onboard-import-preview-page"
    >
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">Review your import</h1>
        <p className="text-sm text-muted-foreground">
          We parsed your file but haven't written anything into your live shop
          yet. Click <strong>Import &amp; Analyze</strong> to save these rows to
          your history and run the analysis.
        </p>
      </div>

      <ImportProgressPanel run={run} isLoading={isLoading} />

      {isError && (
        <Card data-testid="import-preview-load-error">
          <CardContent className="py-3 text-sm text-destructive">
            Could not load this import run. Refresh to retry.
          </CardContent>
        </Card>
      )}

      {run && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Parsed counts</CardTitle>
            <CardDescription>
              These are the rows your CSV produced when we parsed it. Click
              Import &amp; Analyze to save them to your permanent history and
              run the analysis.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Stat label="Total rows" value={run.rows_total} testid="run-rows-total" />
            <Stat label="Chunks" value={run.chunk_total} testid="run-chunks" />
            <Stat
              label="Invalid"
              value={run.rows_invalid}
              testid="run-rows-invalid"
              tone={run.rows_invalid > 0 ? 'warn' : 'muted'}
            />
            <Stat
              label="Duplicates"
              value={run.rows_skipped_duplicate}
              testid="run-rows-skipped"
              tone="muted"
            />
          </CardContent>
        </Card>
      )}

      {run && unresolvedCount > 0 && (
        <Card data-testid="import-preview-unresolved">
          <CardHeader>
            <CardTitle className="text-base">
              {unresolvedCount} {unresolvedCount === 1 ? 'row' : 'rows'} could not be read
            </CardTitle>
            <CardDescription>
              These rows will not be imported. Check them in your file — nothing is
              imported until you confirm you have reviewed them.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="max-h-56 space-y-1 overflow-y-auto text-sm" data-testid="import-preview-unresolved-list">
              {(run.unresolved_rows ?? []).map((u, i) => (
                <li key={`${u.row ?? 'n'}-${i}`} className="break-words">
                  <span className="font-medium">{u.row != null ? `Row ${u.row}` : 'A row'}</span>
                  {u.field ? <span className="text-muted-foreground"> ({u.field})</span> : null}: {u.reason}
                </li>
              ))}
            </ul>
            {(run.unresolved_rows ?? []).length < unresolvedCount && (
              <p className="text-xs text-muted-foreground" data-testid="import-preview-unresolved-more">
                Showing the first {(run.unresolved_rows ?? []).length} of {unresolvedCount}.
              </p>
            )}
            {needsReview ? (
              <Button
                variant="outline"
                onClick={handleAcknowledge}
                disabled={reviewBusy || run.status !== 'uploaded'}
                data-testid="import-preview-acknowledge"
              >
                {reviewBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                I've reviewed these rows — import without them
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="import-preview-acknowledged">
                Reviewed — these rows are left out of the import.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {run?.status === 'failed' && (
        <Card data-testid="import-preview-failed">
          <CardHeader>
            <CardTitle className="text-base">This import failed</CardTitle>
            <CardDescription>
              {run.error_message ?? 'The import stopped before it finished.'} None of it is
              shown as your shop's history. Discard it to remove what was saved, then upload
              a corrected file.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 sm:flex-row">
            <Input
              value={discardReason}
              onChange={(e) => setDiscardReason(e.target.value)}
              placeholder="Why are you discarding it? (e.g. wrong export)"
              aria-label="Reason for discarding this import"
              data-testid="import-preview-discard-reason"
            />
            <Button
              variant="destructive"
              onClick={handleDiscard}
              disabled={discardBusy || discardReason.trim().length < 3}
              data-testid="import-preview-discard"
            >
              {discardBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Discard this import
            </Button>
          </CardContent>
        </Card>
      )}

      {actionError && (
        <Card data-testid="import-preview-action-error">
          <CardContent className="py-3 text-sm text-destructive">{actionError}</CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="outline"
          onClick={() => navigate('/onboard/setup')}
          data-testid="import-preview-back"
        >
          Back to setup choices
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!canContinue}
          data-testid="import-preview-continue"
        >
          {isWorking ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ArrowRight className="mr-2 h-4 w-4" />
          )}
          {stage === 'promoting' && 'Importing…'}
          {stage === 'analyzing' && 'Analyzing…'}
          {stage !== 'promoting' && stage !== 'analyzing' && 'Import & Analyze'}
        </Button>
      </div>
    </div>
  );
};

function Stat({
  label,
  value,
  testid,
  tone = 'normal',
}: {
  label: string;
  value: number;
  testid: string;
  tone?: 'normal' | 'muted' | 'warn';
}) {
  const toneClass =
    tone === 'warn'
      ? 'text-amber-700'
      : tone === 'muted'
        ? 'text-muted-foreground'
        : 'text-foreground';
  return (
    <div className="rounded border bg-muted/40 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${toneClass}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

export default OnboardSetupImportPreview;
